var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useEffect, useContext, useMemo, useRef, useReducer, useCallback, } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuid4 } from 'uuid';
import { VisuallyHidden } from '../components/VisuallyHidden';
import { LiveRegion, } from '../components/LiveRegion';
import { regionReducer, initialRegionState, ACTIONS, } from '../utils/reducerUtils';
import { isValidAnnouncement } from '../utils';
var initialState = {
    announcePolite: function () {
        /* noop */
    },
    announceAssertive: function () {
        /* noop */
    },
};
export var AriaLiveContext = React.createContext(initialState);
export var AriaLiveConsumer = AriaLiveContext.Consumer;
var Portal = React.forwardRef(function (_a, ref) {
    var children = _a.children;
    var _b = __read(useState(null), 2), isDocumentAvailable = _b[0], setIsDocumentAvailable = _b[1];
    useEffect(function () {
        setIsDocumentAvailable(!!document);
    }, []);
    return isDocumentAvailable
        ? createPortal(React.createElement("div", { ref: ref }, children), document.body)
        : null;
});
var portalRef = React.createRef();
/**
 * Ignoring `portalObserverCallback` for code coverage since it is a callback
 * passed to Mutation Observer which can only be mocked in Jest since node
 * does not implement that API. Also, importing this callback directly for
 * testing does not work since there are no direct effects returned from this
 * function but only side effects that can only be observed via a Mutation
 * Observer which, as noted previously, can only be mocked out in Jest.
 */
/* istanbul ignore next */
var portalObserverCallback = function (mutations) {
    mutations.forEach(function (mutation) {
        var _a;
        var isAttributeChange = mutation.type === 'attributes';
        var isDiv = ((_a = portalRef.current) === null || _a === void 0 ? void 0 : _a.tagName) === 'DIV';
        var shouldRemoveAttributes = isAttributeChange && isDiv;
        if (shouldRemoveAttributes) {
            var attributeList = Array.from(portalRef.current.attributes);
            attributeList.forEach(function (attr) {
                var _a;
                (_a = portalRef.current) === null || _a === void 0 ? void 0 : _a.removeAttribute(attr.name);
            });
        }
    });
};
export var isSSR = !(typeof window !== 'undefined' && !!window.document.createElement);
var portalObserver = !isSSR
    ? new MutationObserver(portalObserverCallback)
    : null;
var AriaLiveProvider = function (_a) {
    var children = _a.children;
    var _b = __read(useState("aria-live-regions-".concat(uuid4())), 1), landmarkId = _b[0];
    var sectionRef = useRef(null);
    var ariaLiveContext = useContext(AriaLiveContext);
    var portalDiv = portalRef.current;
    // eslint-disable-next-line consistent-return
    useEffect(function () {
        if (portalObserver && portalDiv) {
            portalObserver.observe(portalDiv, { attributes: true });
            return function () { return portalObserver.disconnect(); };
        }
    }, [portalDiv]);
    var outerProviderDetected = useMemo(function () {
        return (ariaLiveContext.announcePolite !== initialState.announcePolite &&
            ariaLiveContext.announceAssertive !== initialState.announceAssertive);
    }, [ariaLiveContext]);
    var _c = __read(useReducer(regionReducer, initialRegionState), 2), regionState = _c[0], dispatchRegionState = _c[1];
    var queueAnnouncement = useCallback(function (announcement, setting, config) {
        if (isValidAnnouncement(announcement)) {
            // clearAttributes()
            dispatchRegionState({
                type: ACTIONS.ENQUEUE,
                payload: __assign({ announcement: announcement, setting: setting }, (config || {})),
            });
        }
    }, [dispatchRegionState]);
    var announcePolite = useCallback(function (announcement, config) {
        queueAnnouncement(announcement, 'polite', config);
    }, [queueAnnouncement]);
    var announceAssertive = useCallback(function (announcement, config) {
        /**
         * The default role for LiveRegion's is log, which should not be paired with
         * assertive messages. We're forcing the default to alert here to match the
         * vast majority of use cases. More information on this can be found here:
         * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions#Preferring_specialized_live_region_roles
         */
        queueAnnouncement(announcement, 'assertive', __assign(__assign({}, config), { role: (config === null || config === void 0 ? void 0 : config.role) || 'alert' }));
    }, [queueAnnouncement]);
    return (React.createElement(React.Fragment, null, outerProviderDetected ? (children) : (React.createElement(AriaLiveContext.Provider, { value: {
            announcePolite: announcePolite,
            announceAssertive: announceAssertive,
        } },
        children,
        React.createElement(Portal, { ref: portalRef },
            React.createElement(VisuallyHidden, { ref: sectionRef, id: landmarkId, as: "section", "aria-label": "You reached the end of the customer portion of this page. The following content is for ally\u2019s internal team only - please disregard if it doesn\u2019t apply." },
                React.createElement(VisuallyHidden, { "aria-describedby": landmarkId }),
                React.createElement(LiveRegion, __assign({}, regionState.queue[0], { "data-testid": "live-region-0" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[1], { "data-testid": "live-region-1" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[2], { "data-testid": "live-region-2" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[3], { "data-testid": "live-region-3" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[4], { "data-testid": "live-region-4" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[5], { "data-testid": "live-region-5" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[6], { "data-testid": "live-region-6" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[7], { "data-testid": "live-region-7" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[8], { "data-testid": "live-region-8" })),
                React.createElement(LiveRegion, __assign({}, regionState.queue[9], { "data-testid": "live-region-9" }))))))));
};
export { AriaLiveProvider };
//# sourceMappingURL=AriaLiveProvider.js.map